// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Alert from "src/components/shared/alerts/Alert/Alert";
import EstermanChart from "./EstermanChart/EstermanChart";
import EstermanChartLegend from "./EstermanChartLegend/EstermanChartLegend";
import GazeGraph from "../../../GazeGraph/GazeGraph";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import {
  getChartSpots,
  getGazeChartData,
  isTerminatedExamReport,
} from "src/utils/exams/examReportsHelper";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

// Constants
import { examCardEnums, gridTypeEnums } from "src/constants/enums";

const EstermanGraph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.VISUAL_FIELD_ESTERMAN.sectionName,
      }),
    [exam, examReport]
  );

  const dimensions = useComponentDimensions({
    componentId: "#esterman-container",
  });

  const estermanSpots = useMemo(
    () => getChartSpots(examReport) ?? [],
    [examReport]
  );

  const gazeSpots = useMemo(() => {
    const { gazeSpots } = getGazeChartData(examReport);
    return gazeSpots ?? [];
  }, [examReport]);

  const isBinocular = useMemo(
    () => examReport?.exam?.grid === gridTypeEnums.G_120.type,
    [examReport?.exam?.grid]
  );

  return (
    <Grid container gap="20px" id="esterman-container">
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt="20px"
      >
        <EstermanChart
          estermanSpots={estermanSpots}
          containerSize={dimensions?.dynamicWidth}
        />
      </Grid>
      <Grid item xs={12}>
        <EstermanChartLegend
          estermanSpots={estermanSpots}
          isBinocular={isBinocular}
        />
      </Grid>
      <Grid item xs={12}>
        <GazeGraph gazeSpots={gazeSpots} />
      </Grid>
    </Grid>
  );
};

export default EstermanGraph;
